import type { ReactNode } from 'react';
import { createContext, useCallback, useContext } from 'react';
import { buildUrl } from '../utils/buildQuery';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import type {
  VoiceProfileDetailApi,
  VoiceProfileListApi,
  VoiceProfileUpdateApi,
  VoiceProfileCreateApi,
  RealtimeSessionListApi,
  RealtimeSessionDetailApi,
  RealtimeMessageListApi,
} from './VoiceProfileApiProviderTypes';

interface VoiceProfileApiContextType {
  getVoiceProfiles: (skip?: number, limit?: number) => Promise<VoiceProfileListApi>;
  deleteVoiceProfile: (voiceProfileId: string) => Promise<void>;
  createVoiceProfile: (data: VoiceProfileCreateApi) => Promise<VoiceProfileDetailApi>;
  getVoiceProfileById: (voiceProfileId: string) => Promise<VoiceProfileDetailApi>;
  updateVoiceProfile: (
    voiceProfileId: string,
    data: VoiceProfileUpdateApi,
  ) => Promise<VoiceProfileDetailApi>;
  getSessions: (
    voiceProfileId: string,
    skip?: number,
    limit?: number,
  ) => Promise<RealtimeSessionListApi>;
  getSessionById: (voiceProfileId: string, sessionId: string) => Promise<RealtimeSessionDetailApi>;
  getSessionMessages: (
    voiceProfileId: string,
    sessionId: string,
    skip?: number,
    limit?: number,
  ) => Promise<RealtimeMessageListApi>;
}

const VoiceProfileApiContext = createContext<VoiceProfileApiContextType | null>(null);
const baseUrl = '/v1/agents/voice_profiles';

export function VoiceProfileApiProvider({ children }: { children: ReactNode }) {
  const { get, del, patch, put, post } = useApiService(CONFIG.SERVER_ASSISTANT);

  const getVoiceProfiles = useCallback(
    async (skip: number = 0, limit: number = 10): Promise<VoiceProfileListApi> => {
      try {
        const response = await get(
          buildUrl(baseUrl, {
            skip,
            limit,
          }),
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching voice profiles:', error);
        throw error;
      }
    },
    [get],
  );

  const deleteVoiceProfile = useCallback(
    async (voiceProfileId: string): Promise<void> => {
      try {
        await del(`${baseUrl}/${voiceProfileId}`);
      } catch (error) {
        console.error('Error deleting voice profile:', error);
        throw error;
      }
    },
    [del],
  );

  const createVoiceProfile = useCallback(
    async (data: VoiceProfileCreateApi): Promise<VoiceProfileDetailApi> => {
      try {
        const response = await post(`${baseUrl}`, data);
        return response.data;
      } catch (error) {
        console.error('Error creating voice profile:', error);
        throw error;
      }
    },
    [put],
  );

  const getVoiceProfileById = useCallback(
    async (voiceProfileId: string): Promise<VoiceProfileDetailApi> => {
      try {
        const response = await get(`${baseUrl}/${voiceProfileId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching voice profile by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const updateVoiceProfile = useCallback(
    async (voiceProfileId: string, data: VoiceProfileUpdateApi): Promise<VoiceProfileDetailApi> => {
      try {
        const response = await patch(`${baseUrl}/${voiceProfileId}`, data);
        return response.data;
      } catch (error) {
        console.error('Error updating voice profile:', error);
        throw error;
      }
    },
    [patch],
  );

  const getSessions = useCallback(
    async (
      voiceProfileId: string,
      skip: number = 0,
      limit: number = 10,
    ): Promise<RealtimeSessionListApi> => {
      try {
        const response = await get(
          buildUrl(`${baseUrl}/${voiceProfileId}/sessions`, {
            skip,
            limit,
          }),
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching sessions:', error);
        throw error;
      }
    },
    [get],
  );

  const getSessionById = useCallback(
    async (voiceProfileId: string, sessionId: string): Promise<RealtimeSessionDetailApi> => {
      try {
        const response = await get(`${baseUrl}/${voiceProfileId}/sessions/${sessionId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching session by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const getSessionMessages = useCallback(
    async (
      voiceProfileId: string,
      sessionId: string,
      skip: number = 0,
      limit: number = 10,
    ): Promise<RealtimeMessageListApi> => {
      try {
        const response = await get(
          buildUrl(`${baseUrl}/${voiceProfileId}/sessions/${sessionId}/messages`, {
            skip,
            limit,
          }),
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching session messages:', error);
        throw error;
      }
    },
    [get],
  );

  return (
    <VoiceProfileApiContext.Provider
      value={{
        getVoiceProfiles,
        deleteVoiceProfile,
        createVoiceProfile,
        getVoiceProfileById,
        updateVoiceProfile,
        getSessions,
        getSessionById,
        getSessionMessages,
      }}
    >
      {children}
    </VoiceProfileApiContext.Provider>
  );
}

export function useVoiceProfileApi(): VoiceProfileApiContextType {
  const context = useContext(VoiceProfileApiContext);
  if (!context) {
    throw new Error('useVoiceProfileApi must be used within a VoiceProfileApiProvider');
  }
  return context;
}
